import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, Globe, MapPin, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    requirement: '',
    product_interest: '',
    source: 'contact_form',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const product = params.get('product');
    const source = params.get('source');
    if (product || source) {
      setFormData((prev) => ({
        ...prev,
        product_interest: product || prev.product_interest,
        source: source || prev.source,
        requirement: product
          ? `Hi, I'd like a rental quote for ${product}. Please share availability and pricing.`
          : prev.requirement,
      }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.requirement) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        requirement: formData.requirement.trim(),
        source: formData.source || 'contact_form',
      };
      if (formData.product_interest) payload.product_interest = formData.product_interest;
      const { data } = await axios.post(`${API}/inquiries`, payload);
      toast.success(data?.message || 'Thank you! We will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', requirement: '', product_interest: '', source: 'contact_form' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : (typeof detail === 'string' ? detail : 'Could not submit. Please try again or call us directly.');
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="contact-page">
      {/* Hero (teal) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#008080] via-[#0a7878] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-50"></div>
        <div className="spotlight-gold bottom-0 right-0"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline ark-overline-light">// 08 · Open a request</span>
          <h1 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-5xl md:text-7xl leading-[0.88]">
            Let's get on<br />
            <span className="text-[#DAA520]">your site.</span>
          </h1>
          <p className="text-white/85 mt-6 max-w-2xl text-base md:text-lg">
            Tell us what you need. Our team will respond on WhatsApp or phone within working hours.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="relative py-20 md:py-24 bg-gray-50">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-px bg-gray-200 border border-gray-200">
            <div className="lg:col-span-5 bg-white p-8 md:p-10">
              <span className="ark-overline">// Direct line</span>
              <h2 className="font-display font-bold text-[#008080] text-3xl md:text-4xl mt-4 leading-tight">Get in touch.</h2>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Reach us through any channel below — our team responds promptly during working hours.
              </p>

              <div className="mt-8 space-y-px bg-gray-200">
                {[
                  { icon: MapPin, label: 'Address', value: 'Survey No. 148/5, Opp. Sandeep Kate Bunglow, Pimple Saudagar, Pune - 411027' },
                  { icon: Phone, label: 'Phone & WhatsApp', value: '+91 9766601155', href: 'tel:+919766601155' },
                  { icon: Mail, label: 'Email', value: 'info@arkscaffolding.in', href: 'mailto:info@arkscaffolding.in' },
                  { icon: Globe, label: 'Website', value: 'www.arkscaffolding.in', href: 'https://www.arkscaffolding.in' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  const Wrapper = item.href ? 'a' : 'div';
                  return (
                    <Wrapper key={i} {...(item.href ? { href: item.href } : {})} className="bg-white flex items-start gap-4 p-5 hover:bg-gray-50 transition">
                      <div className="w-10 h-10 border border-[#008080]/30 bg-[#008080]/5 flex items-center justify-center flex-shrink-0 rounded">
                        <Icon size={16} className="text-[#008080]" />
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-1">// {item.label}</div>
                        <div className="text-gray-900 text-sm leading-relaxed">{item.value}</div>
                      </div>
                    </Wrapper>
                  );
                })}
              </div>

              <div className="mt-8 bg-[#008080]/5 border border-[#10B981]/40 p-6 rounded">
                <div className="flex items-center gap-3 mb-3">
                  <span className="radar-dot"></span>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#10B981]">// Quick response</span>
                </div>
                <p className="text-[#008080] text-base font-display font-bold mb-2">Reply on WhatsApp.</p>
                <p className="text-gray-600 text-sm mb-5">Get instant replies to your queries from our team.</p>
                <a href="https://wa.me/919766601155" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#10B981] text-white px-5 py-2.5 text-xs font-mono uppercase tracking-widest font-bold hover:bg-[#0EA572] transition rounded">
                  <MessageCircle size={14} /> Chat on WhatsApp
                </a>
              </div>
            </div>

            <div className="lg:col-span-7 bg-white p-8 md:p-12">
              <span className="ark-overline">// Send a message</span>
              <h2 className="font-display font-bold text-[#008080] text-3xl md:text-4xl mt-4 leading-tight">Request a quote.</h2>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="contact-form">
                {formData.product_interest && (
                  <div className="border border-[#DAA520]/40 bg-[#DAA520]/10 px-4 py-3 text-sm font-mono tracking-wide rounded" data-testid="product-interest-banner">
                    <span className="text-gray-600">Quoting:</span> <span className="text-[#B8860B] font-bold">{formData.product_interest}</span>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">// Name *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange} className="ark-input" placeholder="Your full name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">// Phone *</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="ark-input" placeholder="+91 9766601155" data-testid="contact-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">// Email *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="ark-input" placeholder="you@company.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">// Requirement *</label>
                  <textarea name="requirement" required rows="6" value={formData.requirement} onChange={handleChange} className="ark-input resize-none" placeholder="Project type, quantity, duration, location..." data-testid="contact-requirement-input" />
                </div>

                <button type="submit" disabled={isSubmitting} className="ark-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit-btn">
                  <Send size={16} />
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gray-400 text-center mt-2">// We respond within working hours</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="relative py-20 md:py-24 bg-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline">// Find us</span>
          <h2 className="font-display font-bold text-[#008080] text-3xl md:text-4xl mt-4 mb-8 leading-tight">Yard location.</h2>
          <div className="border border-gray-200 overflow-hidden rounded">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3781.7282!2d73.7675!3d18.5894!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDM1JzIxLjgiTiA3M8KwNDYnMDMuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%" height="450" style={{ border: 0 }} allowFullScreen=""
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              title="ARK Scaffolding Location - Pimple Saudagar, Pune"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
